import React, { useEffect } from 'react';
import { Typography } from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import { get, isEmpty, find } from 'lodash';

import { Box, Button, FileUpload } from 'components';
import { StepProps } from 'common/types';
import { RootState } from 'redux-modules/store/rootState';
import { updateInvestmentProcess } from 'redux-modules/investor/Actions';
import {
  uploadFileSignedUrl,
  addInvestorDocumentByName,
  getInvestmentDocuments,
} from 'services';
import history from 'common/utils/history';
import { errorMessageHandler, formatAmount } from 'common/utils/helpers';

import styles from './styles';

interface ReInvestConfirmationProps extends StepProps {
  reInvestPartial: boolean;
  distributionData: any;
}

const ReInvestConfirmation = ({
  handleBack,
  spv,
  investor,
  kycId,
  reInvestPartial,
  distributionData,
}: ReInvestConfirmationProps) => {
  const classes = styles();
  const dispatch = useDispatch();

  const { dealList } = useSelector(({ Deals }: RootState) => Deals);

  const [loadingSignedUrl, setLoadingSignedUrl] = React.useState<boolean>(
    false,
  );
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [documents, setDocuments] = React.useState<any>([]);

  const { investmentProcess } = useSelector(
    ({ Investor }: RootState) => Investor,
  );

  const { investmentDetail } = investor;
  const investmentData = investmentDetail?.find(
    (investment: any) => investment.spv === spv && investment.kycId === kycId,
  );

  const rolloverSourceSpv = get(distributionData, 'spv.id');
  const rolloverInvestmentData = investmentDetail?.find(
    (investment: any) => investment.spv === rolloverSourceSpv,
  );

  const onSubmit = () => {
    if (reInvestPartial && isEmpty(uploadedFiles())) {
      setErrorMessage('Please upload bank details.');
    } else {
      history.push('/dashboard');
    }
  };

  const uploadOnSelect = async (fileuploadReq: any): Promise<void> => {
    setErrorMessage('');
    if (!isEmpty(fileuploadReq)) {
      setIsLoading(true);
      try {
        // eslint-disable-next-line no-undef
        await fetch(get(fileuploadReq, 'url.uploadUrl'), {
          method: 'PUT',
          body: fileuploadReq.file,
        });
        const addDocumentPayload: any = {
          filename: fileuploadReq.filename,
          documentName: 'RCVD_DIST_BANK_DETAILS',
          documentUrl: get(fileuploadReq, 'url.accessUrl'),
          investmentDetailId: get(rolloverInvestmentData, 'id'),
          receivedFullAmount: reInvestPartial,
        };
        const existingDocList: any = uploadedFiles();
        if (!isEmpty(existingDocList))
          addDocumentPayload.id = existingDocList[0].id;
        await addInvestorDocumentByName(addDocumentPayload);
        getInvestmentAllDocument();
        const invProcess = investmentProcess;
        invProcess.wireInstructionUploaded = true;
        dispatch(updateInvestmentProcess(invProcess));
      } catch (err) {
        const message: string = errorMessageHandler(err);
        setErrorMessage(message);
      } finally {
        setIsLoading(false);
      }
    } else {
      setErrorMessage('Please upload bank details.');
    }
  };

  const uploadedFiles = () => {
    if (isEmpty(documents)) return [];
    if (!isEmpty(documents)) {
      return documents.filter(
        (doc: any) => doc.documentName === 'RCVD_DIST_BANK_DETAILS',
      );
    }
  };

  const getInvestmentAllDocument = () => {
    if (!isEmpty(rolloverInvestmentData) && isEmpty(documents)) {
      setErrorMessage('');
      setIsLoading(true);
      getInvestmentDocuments({
        id: get(rolloverInvestmentData, 'id'),
        type: 'NON_DOCU_SIGN_DOCUMENTS',
      })
        .then((res: any) => {
          setDocuments(res);
          setIsLoading(false);
        })
        .catch((err: any) => {
          const message: string = errorMessageHandler(err);
          setErrorMessage(message);
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    getInvestmentAllDocument();
  }, []);

  return (
    <Box className={classes.tradeConfirm}>
      <Typography variant="h3" className={classes.investNowFormHeader}>
        Confirmation
      </Typography>
      {get(investmentProcess, 'investmentApproved') ? (
        <Typography className={classes.tCSTxt}>
          You have previously invested{' '}
          {formatAmount(get(investmentData, 'investmentAmount'))} in{' '}
          {get(find(dealList, { id: spv }), 'deal')} SPV. You can track your
          investments through Portfolio tab on your Dashboard. To invest any
          additional amount in this SPV, please write to us at{' '}
          <a href="mailto: founders@8vdx.com">founders@8vdx.com.</a>
        </Typography>
      ) : (
        <Typography className={classes.tCSTxt}>
          You have successfully rolled over your capital. Thank you.
        </Typography>
      )}
      {reInvestPartial && (
        <>
          <Typography className={classes.tCSTxt}>
            Please upload your bank account details to receive a cash
            distribution of the remaining $
            {(
              get(distributionData, 'balance') -
              get(investmentData, 'investmentAmount')
            ).toFixed(2)}
            .
          </Typography>

          <div style={{ display: 'flex' }}>
            <FileUpload
              getSignedUrl={uploadFileSignedUrl}
              uploadedFiles={uploadedFiles()}
              setLoadingSignedUrl={setLoadingSignedUrl}
              uploadOnSelect={uploadOnSelect}
              requestObject={{
                documentName: 'RCVD_DIST_BANK_DETAILS',
                investmentDetailId: get(rolloverInvestmentData, 'id'),
              }}
              content={
                <Button
                  name="Upload Your Bank Account Details*"
                  className={classes.tCBtn}
                  isLoading={loadingSignedUrl || isLoading}
                />
              }
            />
          </div>
        </>
      )}
      {errorMessage && (
        <Typography
          variant="body1"
          gutterBottom
          className={classes.errorMessage}
        >
          {errorMessage}
        </Typography>
      )}
      <Box className={classes.nextPrevButtonBox}>
        <Button
          variant="outlined"
          className={classes.prevButton}
          name="Previous"
          disabled={loadingSignedUrl || isLoading}
          onClick={() => handleBack()}
        />
        <Button
          className={classes.nextButton}
          name={'Submit'}
          disabled={loadingSignedUrl || isLoading}
          onClick={() => onSubmit()}
        />
      </Box>
    </Box>
  );
};
export default ReInvestConfirmation;
